import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, InviteCodeError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Ticket } from "lucide-react";
import { z } from "zod";
import { countryCodes } from "@/data/countryCodes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import logoSvg from "@/assets/logo.svg";

const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signUpSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
  phoneNumber: z.string()
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number is too long")
    .regex(/^[\d\s-]+$/, "Phone number can only contain digits, spaces, and dashes"),
  addressUae: z.string().min(1, "Address is required"),
  inviteCode: z.string().min(1, "Invite code is required"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

// Error messages for invite code errors
const CODE_ERROR_MESSAGES: Record<InviteCodeError, string> = {
  CODE_REQUIRED: "Le code d'invitation est obligatoire.",
  INVALID_CODE: "Code invalide. Vérifiez le code fourni par la conciergerie.",
  CODE_USED: "Ce code a déjà été utilisé. Demandez un nouveau code.",
  CODE_EXPIRED: "Ce code a expiré. Demandez un nouveau code.",
  CODE_REVOKED: "Ce code a été révoqué. Demandez un nouveau code.",
  EMAIL_ALREADY_EXISTS: "Un compte existe déjà avec cet email.",
  GENERIC: "Impossible de créer le compte. Réessayez.",
};

// Anti-bruteforce delays (in seconds)
const BRUTEFORCE_DELAYS = [1, 2, 4, 8, 10];

export default function AuthPage() {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Anti-bruteforce state
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneCode: "+971",
    phoneNumber: "",
    addressUae: "Dubai, UAE",
    inviteCode: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutUntil) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
        setCountdown(remaining);
        if (remaining === 0) {
          setLockoutUntil(null);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [lockoutUntil]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const applyBruteforceDelay = useCallback(() => {
    const delayIndex = Math.min(failedAttempts, BRUTEFORCE_DELAYS.length - 1);
    const delaySeconds = BRUTEFORCE_DELAYS[delayIndex];
    setLockoutUntil(Date.now() + delaySeconds * 1000);
    setCountdown(delaySeconds);
    setFailedAttempts((prev) => prev + 1);
  }, [failedAttempts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if locked out
    if (lockoutUntil && Date.now() < lockoutUntil) {
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      if (isLogin) {
        const validated = signInSchema.parse({
          email: formData.email,
          password: formData.password,
        });

        const { error } = await signIn(validated.email, validated.password);
        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error("Invalid email or password");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success("Welcome back!");
          navigate("/");
        }
      } else {
        const validated = signUpSchema.parse(formData);
        
        // Normalize phone number (remove spaces and dashes)
        const normalizedPhone = validated.phoneNumber.replace(/[\s-]/g, '');

        const result = await signUp(
          validated.email,
          validated.password,
          validated.firstName,
          validated.lastName,
          validated.inviteCode,
          `${formData.phoneCode}${normalizedPhone}`,
          validated.addressUae
        );

        if (result.error) {
          // Apply bruteforce delay for code-related errors
          if (result.codeError && ['INVALID_CODE', 'CODE_USED', 'CODE_EXPIRED', 'CODE_REVOKED'].includes(result.codeError)) {
            applyBruteforceDelay();
          }
          
          const errorMessage = result.codeError 
            ? CODE_ERROR_MESSAGES[result.codeError] 
            : result.error.message;
          
          toast.error(errorMessage);
          
          // Set field-specific error for invite code
          if (result.codeError && result.codeError !== 'EMAIL_ALREADY_EXISTS' && result.codeError !== 'GENERIC') {
            setErrors((prev) => ({ ...prev, inviteCode: errorMessage }));
          }
          if (result.codeError === 'EMAIL_ALREADY_EXISTS') {
            setErrors((prev) => ({ ...prev, email: errorMessage }));
          }
        } else {
          // Reset failed attempts on success
          setFailedAttempts(0);
          toast.success("Account created successfully!");
          navigate("/chat");
        }
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) {
            fieldErrors[e.path[0] as string] = e.message;
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLocked = lockoutUntil && Date.now() < lockoutUntil;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header with Logo */}
      <header className="flex items-center justify-center py-6 sm:py-8 px-4">
        <Link to="/">
          <img 
            src={logoSvg} 
            alt="Conciergerie.ae" 
            className="h-12 sm:h-16 w-auto"
          />
        </Link>
      </header>

      {/* Form */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pb-6 sm:pb-8">
        <div className="w-full max-w-sm sm:max-w-md animate-fade-in">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-2">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {isLogin
                ? "Sign in to access your concierge"
                : "Join our exclusive concierge service"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      autoComplete="given-name"
                    />
                    {errors.firstName && (
                      <p className="text-xs text-destructive">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      autoComplete="family-name"
                    />
                    {errors.lastName && (
                      <p className="text-xs text-destructive">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Phone Field with Country Code Selector */}
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.phoneCode}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, phoneCode: value }))}
                    >
                      <SelectTrigger className="w-[120px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {countryCodes.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            <span className="flex items-center gap-2">
                              <span>{country.flag}</span>
                              <span>{country.code}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="50 123 4567"
                      autoComplete="tel-national"
                      className="flex-1"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-xs text-destructive">{errors.phoneNumber}</p>
                  )}
                </div>

                {/* Address Field */}
                <div className="space-y-2">
                  <Label htmlFor="addressUae">Address (UAE)</Label>
                  <Input
                    id="addressUae"
                    name="addressUae"
                    value={formData.addressUae}
                    onChange={handleChange}
                    placeholder="Dubai, UAE"
                    autoComplete="street-address"
                  />
                  {errors.addressUae && (
                    <p className="text-xs text-destructive">{errors.addressUae}</p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password}</p>
              )}
            </div>

            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* Invite Code Field */}
                <div className="space-y-2">
                  <Label htmlFor="inviteCode" className="flex items-center gap-2">
                    <Ticket size={14} className="text-primary" />
                    Invite Code
                  </Label>
                  <Input
                    id="inviteCode"
                    name="inviteCode"
                    value={formData.inviteCode}
                    onChange={handleChange}
                    placeholder="Enter your invite code"
                    autoComplete="off"
                    className={errors.inviteCode ? "border-destructive" : ""}
                  />
                  {errors.inviteCode && (
                    <p className="text-xs text-destructive">{errors.inviteCode}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Contact the concierge to obtain your invite code
                  </p>
                </div>
              </>
            )}

            <Button
              type="submit"
              variant="gold"
              size="lg"
              className="w-full mt-6"
              disabled={loading || !!isLocked}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  <span>{isLogin ? "Signing in..." : "Creating account..."}</span>
                </>
              ) : isLocked ? (
                <span>Retry in {countdown}s</span>
              ) : (
                <span>{isLogin ? "Sign In" : "Create Account"}</span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setErrors({});
                setFailedAttempts(0);
                setLockoutUntil(null);
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {isLogin ? (
                <>
                  Don&apos;t have an account?{" "}
                  <span className="text-primary font-medium">Sign up</span>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <span className="text-primary font-medium">Sign in</span>
                </>
              )}
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-xs text-muted-foreground">
          By continuing, you agree to our Terms of Service
        </p>
      </footer>
    </div>
  );
}